// istanbul ignore file
import React, { useState, useEffect } from 'react'
import { APIConfiguration } from '../../../utils/apiConfiguration'
import AuthError from '../../../components/ErrorComponents/AuthError'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import { useWebSocket } from '../../../contexts/WebSocketContext'
import { updateRegisterReadingsAction } from '../redux/getDeviceStatusSummaryRegisterSlice'
import { updateKPIDataAction } from '../redux/getDeviceStatusSummaryKPISlice'
import { type HealthSummaryState } from '../redux/getHealthSummarySlice'
import type {
  DashboardModbusDataUpdate,
  DashboardKPIDataUpdate,
  RegisterReading,
  KPIData
} from '../../../types/websocket.types'
import {
  Checkbox,
  FormControlLabel,
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Tooltip,
  Typography
} from '@mui/material'
import { Button } from '../../../components/Button'
import { type ConnectedProps, connect } from 'react-redux'
import { type RootState } from '../../../store'
import { getDeviceStatusRegisterSummaryService, getDeviceStatusKPISummaryService, getDeviceStatusOnlineService } from '../../device-dashboard/redux/actionCreator'
import { type IDeviceRegisterStatusState } from '../redux/getDeviceStatusSummaryRegisterSlice'
import { type IDeviceKPIStatusState } from '../redux/getDeviceStatusSummaryKPISlice'
import ModelPopup from '../../../components/Modal'
import refresh from '../../../assets/refresh.svg'
import { useSelector, useDispatch } from 'react-redux'
import { updateSaveDefaultObject } from '../../../utils/updateSaveDefaultObject'
import { fetchParameterConfiguration } from '../../services/redux/ParameterConfigurationActionCreators'

const maximumSelection = 6
const valueMaxLen = 30
const unitsMaxLen = 30

const DeviceStatusCard = (props: AllProps): JSX.Element => {
  const [isOpen, setIsOpen] = useState(false)
  const handleOpenMenu = (): void => {
    setIsOpen(!isOpen)
  }

  const [device, setDevice] = useState<any>(null)
  const [selectedRegisterIds, setSelectedRegisterIds] = useState<string[]>([])
  const [selectedKpiIds, setSelectedKpiIds] = useState<string[]>([])
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [subscribedDeviceId, setSubscribedDeviceId] = useState<string | null>(null)
  const [lastProcessedMessageId, setLastProcessedMessageId] = useState<string | null>(null)
  const [lastUpdateTime, setLastUpdateTime] = useState<number>(0)
  const GetDeviceParameters = useSelector(
    (state: RootState) => state.getParameterConfigurationData
  )
  const GetSaveDefaultWidgetStatus = useSelector((state: RootState) => state.saveDefaultWidgetData)
  const dispatch = useDispatch()

  useEffect(() => {
    // Only load online status initially - don't auto-load all parameters
    props.getDeviceStatusOnlineService(props.currentDeviceId)

    // Load parameter configuration for the selection dialog
    props.fetchAllParameters()
  }, [props.currentDeviceId])

  const saveDefaultViewObject = useSelector((state: RootState) => state.saveDefaultViewObject?.saveDefaultViewObject)

  useEffect(() => {
    setSelectedRegisterIds([])
    setSelectedKpiIds([])
    props.getDeviceStatusRegisterSummaryService(
      props.parentSiteId,
      props.currentDeviceId,
      '',
      true // Use isDefault=true for default load
    )
    props.getDeviceStatusKPISummaryService(
      props.parentSiteId,
      props.currentDeviceId,
      '',
      true // Use isDefault=true for default load
    )
  }, [props.currentDeviceId, props.GoLive])

  // Separate useEffect for health summary data changes
  useEffect(() => {
    // Don't trigger API calls if the parameter selection dialog is open
    if (isOpen) return

    if (props.healthSummaryData?.data?.data != null) {
      props.getDeviceStatusRegisterSummaryService(
        props.parentSiteId,
        props.currentDeviceId,
        '',
        true // Use isDefault=true for default load
      )
      props.getDeviceStatusKPISummaryService(
        props.parentSiteId,
        props.currentDeviceId,
        '',
        true // Use isDefault=true for default load
      )
    }
  }, [props.healthSummaryData])

  useEffect(() => {
    if (isOpen || (props.deviceStatusRegisterSummayData?.data !== null && props.deviceStatusRegisterSummayData?.data?.asset_id !== props.currentDeviceId) || (props.deviceStatusKPISummayData?.data !== null && props.deviceStatusKPISummayData?.data?.asset_id !== props.currentDeviceId)) return

    const saveDefaultViewDeviceStatus = saveDefaultViewObject?.assetDashBoardDefaultView?.filter(e => e.widgetId === 'deviceStatus')?.[0]
    let tmpSelectedRegisterIds = [...selectedRegisterIds]
    let tmpSelectedKpiIds = [...selectedKpiIds]
    props.deviceStatusRegisterSummayData?.data?.registerReadings?.forEach((item: any, index: number) => {
      if (saveDefaultViewDeviceStatus?.devices?.[0]?.cimCodeIds?.includes(item.cimCodeId) === true) {
        tmpSelectedRegisterIds = tmpSelectedRegisterIds.includes(item.cimCodeId) ? tmpSelectedRegisterIds : [...tmpSelectedRegisterIds, item.cimCodeId]
      } else {
        tmpSelectedRegisterIds = tmpSelectedRegisterIds.includes(item.cimCodeId) ? tmpSelectedRegisterIds?.filter((selectedId) => selectedId !== item.cimCodeId) : [...tmpSelectedRegisterIds]
      }
      if (props.deviceStatusRegisterSummayData?.data?.registerReadings?.length - 1 === index) {
        if (tmpSelectedRegisterIds.length + tmpSelectedKpiIds.length <= maximumSelection) { setSelectedRegisterIds(tmpSelectedRegisterIds) }
      }
    })
    props.deviceStatusKPISummayData?.data?.kpi_data?.forEach((item: any, index: number) => {
      if (saveDefaultViewDeviceStatus?.devices?.[0]?.kpidefinitionIds?.includes(item.kpi_definition_id) === true) {
        tmpSelectedKpiIds = tmpSelectedKpiIds.includes(item.kpi_definition_id) ? tmpSelectedKpiIds : [...tmpSelectedKpiIds, item.kpi_definition_id]
      } else {
        tmpSelectedKpiIds = tmpSelectedKpiIds.includes(item.kpi_definition_id) ? tmpSelectedKpiIds?.filter((selectedId) => selectedId !== item.kpi_definition_id) : [...tmpSelectedKpiIds]
      }
      if (props.deviceStatusKPISummayData?.data?.kpi_data?.length - 1 === index) {
        if (tmpSelectedRegisterIds.length + tmpSelectedKpiIds.length <= maximumSelection) { setSelectedKpiIds(tmpSelectedKpiIds) }
      }
    })
  }, [
    props.deviceStatusRegisterSummayData?.data?.registerReadings,
    props.deviceStatusKPISummayData?.data?.kpi_data,
    saveDefaultViewObject?.assetDashBoardDefaultView]) //  Don't include any other dependencies

  useEffect(() => {
    const findDevice = (data: any, id: string): any => {
      if (data.gateway !== undefined && Array.isArray(data.gateway)) {
        for (const gateway of data.gateway) {
          if (gateway.device !== undefined && Array.isArray(gateway.device)) {
            for (const device of gateway.device) {
              if (device.id === id) {
                return device
              }
            }
          }
        }
      }
      return null
    }
    if (!GetDeviceParameters?.isLoading && GetDeviceParameters?.parameters !== undefined) {
      const foundDevice = findDevice(GetDeviceParameters?.parameters, props.currentDeviceId)
      setDevice(foundDevice)
    }
  }, [props.currentDeviceId, GetDeviceParameters?.parameters])

  const handleCheckboxClick = (event: React.MouseEvent): void => {
    event.stopPropagation()
  }

  const handleChildCheckboxChange = (id: string, type: 'register' | 'kpi'): void => {
    if (
      selectedRegisterIds.length + selectedKpiIds.length >= maximumSelection &&
      !selectedRegisterIds.includes(id) &&
      !selectedKpiIds.includes(id)
    ) {
      setIsModalOpen(true)
      return
    }

    if (type === 'register') {
      setSelectedRegisterIds((prevSelectedRegisterIds) => {
        if (prevSelectedRegisterIds.includes(id)) {
          return prevSelectedRegisterIds.filter(
            (selectedId) => selectedId !== id
          )
        } else {
          return [...prevSelectedRegisterIds, id]
        }
      })
    } else {
      setSelectedKpiIds((prevSelectedKpiIds) => {
        if (prevSelectedKpiIds.includes(id)) {
          return prevSelectedKpiIds.filter((selectedId) => selectedId !== id)
        } else {
          return [...prevSelectedKpiIds, id]
        }
      })
    }
  }

  const handleHierarchyMenuApply = (): void => {
    setIsOpen(false)

    // Only fetch data for selected parameters/KPIs
    if (selectedRegisterIds.length > 0) {
      props.getDeviceStatusRegisterSummaryService(
        props.parentSiteId,
        props.currentDeviceId,
        selectedRegisterIds.toString(),
        false // Use isDefault=false since user made specific selections
      )
    }

    if (selectedKpiIds.length > 0) {
      props.getDeviceStatusKPISummaryService(
        props.parentSiteId,
        props.currentDeviceId,
        selectedKpiIds.toString(),
        false // Use isDefault=false since user made specific selections
      )
    }

    const selectedDefaultData = {
      widgetId: 'deviceStatus',
      displayname: 'deviceStatus card',
      devices: [
        {
          deviceId: props.currentDeviceId,
          cimCodeIds: selectedRegisterIds,
          kpidefinitionIds: selectedKpiIds
        }
      ],
      assets: []
    }
    // update savedefault view redux state
    updateSaveDefaultObject(saveDefaultViewObject, selectedDefaultData, 'deviceStatus')
    props.getDeviceStatusOnlineService(props.currentDeviceId)
    props.fetchAllParameters()
  }

  const handleHierarchyMenuCancel = (): void => {
    setIsOpen(false)
  }

  const handleClearAll = (): void => {
    setSelectedRegisterIds([])
    setSelectedKpiIds([])
  }

  const generateDisplayText = (textInput: string, length: number): any => {
    if (textInput !== undefined) {
      const textInputStr = textInput.toString()
      const isTextLong = textInputStr.length > length
      return isTextLong ? `${textInputStr.substring(0, length)}...` : textInputStr
    }
  }

  const isToolTipVisible = (textInput: string, length: number): any => {
    if (textInput !== undefined) {
      return textInput.toString().length > length ? textInput : ''
    }
  }
  const displayText = (value: any, formattedValue: any, units: any, executionStatus: any): JSX.Element | string => {
    // If formattedValue is null or undefined, show value; if both are null/undefined, show 'NaN'
    let displayValue: any
    if (formattedValue === null || formattedValue === undefined || (typeof formattedValue === 'string' && formattedValue.trim() === '')) {
      displayValue = value
    } else {
      displayValue = formattedValue
    }
    const isBoolean = typeof units === 'string' && units.toLowerCase() === 'boolean'

    if (
      displayValue === null ||
      displayValue === undefined ||
      (executionStatus !== null && executionStatus === 'Failure') ||
      (executionStatus === null && isBoolean === null)
    ) {
      return 'NaN'
    }
    if (isBoolean) {
      const numValue = Number(displayValue)
      if (numValue === 0) {
        return `Normal (${numValue})`
      } else if (numValue === 1) {
        return `Fail (${numValue})`
      }
    }
    return (
      <>
        <Tooltip title={isToolTipVisible(displayValue, valueMaxLen)}>
          <span className='title'>{generateDisplayText(displayValue, valueMaxLen)}</span>
        </Tooltip>
        <Tooltip title={isToolTipVisible(units, unitsMaxLen)}>
          <span className='units'>{generateDisplayText(units, unitsMaxLen)}</span>
        </Tooltip>
      </>
    )
  }

  const deviceStatusMain = (): JSX.Element => {
    switch (true) {
      case props.deviceStatusRegisterSummayData?.isLoading || props.deviceStatusKPISummayData?.isLoading || GetSaveDefaultWidgetStatus?.isLoading:
        return (
          <div className='progress-bar-custom' data-testid='progress-bar'>
            <CircularProgress />
          </div>
        )

      case props.deviceStatusRegisterSummayData?.httpStatus ===
        APIConfiguration.UNAUTHORIZED_HTTP_STATUS && props.deviceStatusKPISummayData?.httpStatus ===
        APIConfiguration.UNAUTHORIZED_HTTP_STATUS:
        return (
          <div className='authError' data-testid='auth-error'>
            <AuthError className='paramterError' errorMessage='Unauthorized' />
          </div>
        )

      case props.deviceStatusRegisterSummayData?.httpStatus ===
        APIConfiguration.ACCESS_FORBIDDEN_HTTP_STATUS && props.deviceStatusKPISummayData?.httpStatus ===
        APIConfiguration.ACCESS_FORBIDDEN_HTTP_STATUS:
        return (
          <div className='authError' data-testid='access-forbidden-error'>
            <AuthError
              className='paramterError'
              errorMessage='accessForbidden'
            />
          </div>
        )

      case props.deviceStatusRegisterSummayData?.httpStatus ===
        APIConfiguration.NO_DATA_FOUND_HTTP_STATUS && (props.deviceStatusKPISummayData?.httpStatus ===
        APIConfiguration.NO_DATA_FOUND_HTTP_STATUS):
        return (
          <div style={{ height: 'auto' }} className='authError' data-testid='no-data-present-error'>
            <AuthError className='paramterError' errorMessage='configurationNotFound' />
          </div>
        )

      case props.deviceStatusRegisterSummayData?.httpStatus ===
        APIConfiguration.NO_SELECTION && props.deviceStatusKPISummayData?.httpStatus ===
        APIConfiguration.NO_SELECTION:
        return (
          <div className='authError' data-testid='no-data-present-error'>
            <AuthError className='paramterError' errorMessage='NoParameterSelection' />
          </div>
        )

      case props.deviceStatusRegisterSummayData?.data === undefined || props.deviceStatusKPISummayData?.data === undefined:
        return (
          <div className='authError' data-testid='data-undefined-error'>
            <AuthError className='paramterError' errorMessage='configurationNotFound' />
          </div>
        )

      case (props.deviceStatusRegisterSummayData?.data !== undefined &&
        props.deviceStatusRegisterSummayData?.httpStatus ===
        APIConfiguration.SUCCESS_HTTP_STATUS) || (props.deviceStatusKPISummayData?.httpStatus ===
        APIConfiguration.SUCCESS_HTTP_STATUS): {
        // Check if we have any selected parameters to display
        const hasSelectedKpis = selectedKpiIds.length > 0 && Array.isArray(props.deviceStatusKPISummayData?.data?.kpi_data)
        const hasSelectedRegisters = selectedRegisterIds.length > 0 && Array.isArray(props.deviceStatusRegisterSummayData?.data?.registerReadings)

        if (!hasSelectedKpis && !hasSelectedRegisters) {
          return (
            <div className='authError' data-testid='no-parameters-selected'>
              <AuthError className='paramterError' errorMessage='NoParameterSelection' />
            </div>
          )
        }

        return (
          <div className='inner-wrapper'>
            {/* Only render selected KPI data */}
            {hasSelectedKpis &&
              props.deviceStatusKPISummayData.data?.kpi_data
                .filter((kpi: any) => selectedKpiIds.includes(kpi.kpi_definition_id))
                .map((kpi: any, index: number) => (
                  <div className='DeviceStatusCard' key={kpi.kpi_definition_id ?? index}>
                    <h5 style={{ color: kpi.colorValue ?? '#fff' }} className='name'>
                      {displayText(
                        kpi.kpi_value ?? kpi.value,
                        kpi.formattedValue,
                        (kpi.formattedValue !== null && kpi.formattedValue !== undefined && typeof kpi.formattedValue === 'string' && kpi.formattedValue !== '') ? '' : (kpi.units ?? ''),
                        kpi.execution_status
                      )}
                    </h5>
                    <Tooltip title={isToolTipVisible(kpi.kpi_name ?? kpi.kpiName ?? '', 30)}>
                      <h5 className='subname'>{generateDisplayText(kpi.kpi_name ?? kpi.kpiName ?? '', 30)}</h5>
                    </Tooltip>
                  </div>
                ))}
            {/* Only render selected register readings */}
            {hasSelectedRegisters &&
              props.deviceStatusRegisterSummayData.data?.registerReadings
                .filter((register: any) => selectedRegisterIds.includes(register.cimCodeId))
                .map((register: any, index: number) => (
                  <div className='DeviceStatusCard' key={register.cimCodeId ?? index}>
                    <h5 style={{ color: register.colorValue ?? '#fff' }} className='name'>
                      {displayText(
                        register.indexValue,
                        register.formattedValue,
                        (register.formattedValue !== null && register.formattedValue !== undefined && typeof register.formattedValue === 'string' && register.formattedValue !== '') ? '' : (register.units ?? ''),
                        register.execution_status
                      )}
                    </h5>
                    <Tooltip title={isToolTipVisible(register.registerName ?? register.parameterName ?? '', 30)}>
                      <h5 className='subname'>{generateDisplayText(register.registerName ?? register.parameterName ?? '', 30)}</h5>
                    </Tooltip>
                  </div>
                ))}
          </div>
        )
      }

      default:
        return (
          <div className='authError' data-testid='data-undefined-error'>
            <AuthError className='paramterError' errorMessage='NoDataPresent' />
          </div>
        )
    }
  }

  // Listen for real-time device register and KPI updates via WebSocket
  const { messages, subscribeToDevice, subscribeToDeviceKPIs, isConnected } = useWebSocket()

  // Subscribe to WebSocket updates for the current device
  useEffect(() => {
    if (isConnected && props.currentDeviceId !== undefined && props.currentDeviceId !== null && props.currentDeviceId !== '') {
      // Only subscribe if we haven't already subscribed to this device
      if (subscribedDeviceId !== props.currentDeviceId) {
        // Subscribe to both register and KPI updates
        Promise.all([
          subscribeToDevice(props.currentDeviceId),
          subscribeToDeviceKPIs(props.currentDeviceId)
        ])
          .then(() => {
            setSubscribedDeviceId(props.currentDeviceId)
          })
          .catch(() => {
            // Silently ignore WebSocket subscription errors
            // as they are not critical for the component functionality
          })
      }
    } else {
      // Reset subscription state when disconnected or no device
      if (subscribedDeviceId !== null) {
        setSubscribedDeviceId(null)
      }
    }
  }, [isConnected, props.currentDeviceId, subscribeToDevice, subscribeToDeviceKPIs])

  // Reset subscription state when device changes
  useEffect(() => {
    if (subscribedDeviceId !== null && subscribedDeviceId !== props.currentDeviceId) {
      setSubscribedDeviceId(null)
      setLastProcessedMessageId(null) // Reset message tracking too
    }
  }, [props.currentDeviceId, subscribedDeviceId])

  useEffect(() => {
    // Process only new WebSocket messages with throttling
    if (messages.length > 0) {
      const latestMessage = messages[messages.length - 1]
      const now = Date.now()

      // Throttle updates to maximum once every 500ms per device
      if (now - lastUpdateTime < 500) {
        return
      }

      // Create a unique message ID based on data content
      const messageId = latestMessage.correlationId ??
        `${latestMessage.type}-${String(latestMessage.data?.asset_id ?? '')}-${JSON.stringify(latestMessage.data?.registerReadings?.map((r: any) => ({ id: r.cimCodeId, value: r.value, formattedValue: r.formattedValue })) ?? [])}`

      // Skip if we've already processed this exact message content
      if (messageId === lastProcessedMessageId) {
        return
      }

      // Handle dashboard modbus data updates for register readings
      if (latestMessage.type === 'DASHBOARD_MODBUS_DATA_UPDATE' && Boolean(latestMessage.data)) {
        const data = latestMessage.data as DashboardModbusDataUpdate

        // Check if this message is for our current device (using asset_id)
        if (data.asset_id === props.currentDeviceId) {
          // Update register readings if available
          if (Array.isArray(data.registerReadings)) {
            // Filter register readings to only include those that are currently selected
            // or already exist in the state (to handle both new selections and updates)
            const filteredReadings = data.registerReadings.filter((reading: RegisterReading) => {
              const isSelected = selectedRegisterIds.includes(reading.cimCodeId)
              const existsInState = Boolean(props.deviceStatusRegisterSummayData?.data?.registerReadings?.some(
                (existing: any) => existing.cimCodeId === reading.cimCodeId
              ))
              return isSelected || existsInState
            })

            if (filteredReadings.length > 0) {
              // Only dispatch if there are actual changes in the data
              const hasDataChanges = filteredReadings.some((newReading: RegisterReading) => {
                const existingReading = props.deviceStatusRegisterSummayData?.data?.registerReadings?.find(
                  (existing: any) => existing.cimCodeId === newReading.cimCodeId
                )
                return existingReading === null || existingReading === undefined ||
                       existingReading.value !== newReading.value ||
                       existingReading.formattedValue !== newReading.formattedValue ||
                       existingReading.colorValue !== newReading.colorValue
              })

              if (hasDataChanges) {
                dispatch(updateRegisterReadingsAction({
                  deviceId: props.currentDeviceId,
                  registerReadings: filteredReadings
                }))
                setLastUpdateTime(now) // Update the throttle timestamp only on actual dispatch
              }
            }
          }
          // Mark this message as processed
          setLastProcessedMessageId(messageId)
        }
      }

      // Handle dashboard KPI data updates
      if (latestMessage.type === 'DASHBOARD_KPI_DATA_UPDATE' && Boolean(latestMessage.data)) {
        const data = latestMessage.data as DashboardKPIDataUpdate

        // Check if this message is for our current device (using asset_id)
        if (data.asset_id === props.currentDeviceId) {
          // Update KPI data if available
          if (Array.isArray(data.kpi_data)) {
            // Filter KPI data to only include those that are currently selected
            // or already exist in the state (to handle both new selections and updates)
            const filteredKpiData = data.kpi_data.filter((kpi: KPIData) => {
              const isSelected = selectedKpiIds.includes(kpi.kpi_definition_id)
              const existsInState = Boolean(props.deviceStatusKPISummayData?.data?.kpi_data?.some(
                (existing: any) => existing.kpi_definition_id === kpi.kpi_definition_id
              ))
              return isSelected || existsInState
            })

            if (filteredKpiData.length > 0) {
              // Only dispatch if there are actual changes in the data
              const hasKpiChanges = filteredKpiData.some((newKpi: KPIData) => {
                const existingKpi = props.deviceStatusKPISummayData?.data?.kpi_data?.find(
                  (existing: any) => existing.kpi_definition_id === newKpi.kpi_definition_id
                )
                return existingKpi === null || existingKpi === undefined ||
                       existingKpi.kpi_value !== newKpi.kpi_value ||
                       existingKpi.formattedValue !== newKpi.formattedValue ||
                       existingKpi.colorValue !== newKpi.colorValue
              })

              if (hasKpiChanges) {
                dispatch(updateKPIDataAction({
                  deviceId: props.currentDeviceId,
                  kpi_data: filteredKpiData
                }))
                setLastUpdateTime(now) // Update the throttle timestamp only on actual dispatch
              }
            }
          }
        }
      }

      // Mark this message as processed
      setLastProcessedMessageId(messageId)
    }
  }, [messages.length, props.currentDeviceId, selectedRegisterIds, props.deviceStatusRegisterSummayData?.data, dispatch, lastUpdateTime, selectedKpiIds, props.deviceStatusKPISummayData?.data])

  return (
    <div className="box-wrapper">
      <div className="header-top">
        <div className="left">
          <h6
            style={{
              marginTop: '-15px',
              textAlign: 'left',
              color: '#F0F0F0',
              margin: 0
            }}
            className="heading title-box"
          >
            DEVICE DETAILS
          </h6>
        </div>
        <div className="right dropdown-host">
          <span>
            <MoreVertIcon onClick={handleOpenMenu} data-testid="show-menu-btn" />
          </span>
        </div>
      </div>
      {deviceStatusMain()}
      <Dialog open={isOpen} onClose={handleHierarchyMenuCancel} maxWidth='md' fullWidth
        sx={{
          '& .MuiDialog-paper': {
            width: '370px',
            height: '500px',
            backgroundColor: '#252525',
            color: '#f0f0f0'
          }
        }}
      >
        <DialogTitle style={{ textTransform: 'uppercase', fontFamily: 'Honeywell Sans Web', fontSize: '16px' }} >Parameter Library</DialogTitle>
        <span style={{ color: '#64C3FF', cursor: 'pointer' }} onClick={handleClearAll} data-testid="clear-menu-selection-btn">
          <img src={refresh} height={20} width={20} style={{ marginLeft: '23px', marginRight: '6px', paddingTop: '3px' }} />
          Clear all
        </span>
        <Divider sx={{ backgroundColor: '#202020', border: '1px solid #202020', margin: '10px 0px' }} />
        <DialogContent>
          <div className='hierarchy-menu-content' >
            {device !== null
              ? (
                <Box>
                  <Box display='flex' justifyContent='space-between' alignItems='center'>
                      <Typography style={{ padding: '15px 0px', fontFamily: 'Honeywell Sans Web', fontSize: '16px' }}> {device.name} </Typography>
                  </Box>
                  <div style={{ marginLeft: '9%' }}>
                    {Array.isArray(device.registers) && device.registers?.map((register: any) => (
                      <FormControlLabel
                        key={register.cimCodeId}
                        control={
                          <Checkbox
                            checked={selectedRegisterIds.includes(register.cimCodeId)}
                            onChange={() => {
                              handleChildCheckboxChange(register.cimCodeId, 'register')
                            }}
                            style={{ color: '#64C3FF', fontFamily: 'Honeywell Sans Web', fontSize: '16px' }}
                            className='hon-std-font paramCheckbox'
                          />
                        }
                        label={register.parameterName}
                        onClick={handleCheckboxClick}
                        className='paramLable'
                        data-testid="register-checkbox"
                      />
                    ))}
                    {Array.isArray(device.kpis) && device.kpis.map((kpi: any) => (
                      <FormControlLabel
                        key={kpi.kpiDefinitionId}
                        control={
                          <Checkbox
                            checked={selectedKpiIds.includes(kpi.kpiDefinitionId)}
                            onChange={() => {
                              handleChildCheckboxChange(kpi.kpiDefinitionId, 'kpi')
                            }}
                            style={{ color: '#64C3FF', fontFamily: 'Honeywell Sans Web', fontSize: '16px' }}
                            className='hon-std-font paramCheckbox'
                          />
                        }
                        label={kpi.parameterName}
                        onClick={handleCheckboxClick}
                        className='paramLable'
                        data-testid="kpi-checkbox"
                      />
                    ))}
                  </div>
                </Box>
                )
              : (
                <div className='authError' data-testid='data-undefined-error'>
                  <AuthError
                    className='paramterError'
                    errorMessage='NoDataPresent'
                  />
                </div>
                )
            }
            {isModalOpen && (
              <ModelPopup
                buttonTypeNegative='hollow'
                title={'Parameter Selection'}
                LabelPositive={'Ok'}
                onClickPositive={() => {
                  setIsModalOpen(false)
                }}
                onClose={() => {
                  setIsModalOpen(false)
                }}
              >
                <p style={{ paddingTop: '1.2em' }}>
                  Please select only {maximumSelection} parameters
                </p>
              </ModelPopup>
            )}
          </div>
        </DialogContent>
        <Divider sx={{ backgroundColor: '#202020', border: '1px solid #202020', margin: '10px 0px' }} />
        <DialogActions sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Button onClick={handleHierarchyMenuCancel} type='hollow' data-testid="menu-cancel-btn" aria-label="Cancel">Cancel</Button>
          <Button className='apply-btn' onClick={handleHierarchyMenuApply} data-testid="menu-apply-btn" aria-label="Apply">Apply</Button>
        </DialogActions>
      </Dialog>
    </div>
  )
}
interface DispatchToProps {
  getDeviceStatusRegisterSummaryService: (
    siteId: string,
    deviceIds: string,
    cimCodeIDs: string,
    isDefault: boolean
  ) => void
  getDeviceStatusKPISummaryService: (
    siteId: string,
    deviceIds: string,
    kpiDefinitionIds: string,
    isDefault: boolean
  ) => void
  getDeviceStatusOnlineService: (deviced: string) => void
  getDeviceParameterService: (siteID: string, assetID: string) => void
}

const mapDispatchToProps = (dispatch: any): DispatchToProps => ({
  getDeviceStatusRegisterSummaryService: (
    siteId: string,
    deviceIds: string,
    cimCodeIDs: string,
    isDefault: boolean
  ) =>
    dispatch(
      getDeviceStatusRegisterSummaryService(
        siteId,
        deviceIds,
        cimCodeIDs,
        isDefault
      )
    ),
  getDeviceStatusKPISummaryService: (
    siteId: string,
    deviceIds: string,
    kpiDefinitionIds: string,
    isDefault: boolean
  ) =>
    dispatch(
      getDeviceStatusKPISummaryService(
        siteId,
        deviceIds,
        kpiDefinitionIds,
        isDefault
      )
    ),
  getDeviceStatusOnlineService: (deviced: string) =>
    dispatch(getDeviceStatusOnlineService(deviced)),
  getDeviceParameterService: (siteID: string, assetID: string) =>
    dispatch(fetchParameterConfiguration(siteID, assetID))

})

// eslint-disable-next-line @typescript-eslint/no-empty-interface
interface StateToProps {
  deviceStatusRegisterSummayData: IDeviceRegisterStatusState
  deviceStatusKPISummayData: IDeviceKPIStatusState
  healthSummaryData: HealthSummaryState
}

const mapStateToProps = (state: RootState): StateToProps => ({
  deviceStatusRegisterSummayData: state.deviceStatusRegisterSummaryState,
  deviceStatusKPISummayData: state.deviceStatusKPISummaryState,
  healthSummaryData: state.HealthSummaryState
})
const connector = connect(mapStateToProps, mapDispatchToProps)
interface IDeviceStatusProps {
  currentDeviceId: string
  parentSiteId: string
  parentAssetId: string
  GoLive: boolean
  fetchAllParameters: () => void
}

type PropsFromRedux = ConnectedProps<typeof connector>
type AllProps = PropsFromRedux & IDeviceStatusProps

export default connector(DeviceStatusCard)
